import Long from 'long';
import protobufjs from 'protobufjs';
import { assertCanHandle, isNil, TimedMap, TimedMapCallbackAction } from '@lucidlink/tscore';
import { isLevelEnabled, Logging, LogLevel } from '#common/logging';
import { withStats } from '#common/stats';
import { ClientVersionError, ConflictError, ConnectionError, FilespaceEpochError, InternalError, InvalidArgumentError, InvalidKeyError, InvalidStateError, IoError, TerminateClientError, UnauthorizedError, UnknownError, } from '#common/types';
import { canHandleAllErrors, getVersion, longToBigInt } from '#common/util';
import { ConnectionImpl } from './connection-impl';
import { ServiceBaseProto } from './proto/service';
import { NetworkStats } from './types';
import { ConnectionState } from './types/connection';
import { ErrorResponseError, RequestTimeoutError } from './types/errors';
import { WrappedProtobufMessage } from './wrapped-protobuf-message';
const SECOND = 1000;
const MINUTE = 60 * SECOND;
const DEFAULT_REQUEST_TIMEOUT = MINUTE;
const DEFAULT_REQUEST_TIMEOUT_CHECK_INTERVAL = SECOND;
const DEFAULT_RECONNECT_INTERVAL = SECOND;
const DEFAULT_CONNECTION_TIMEOUT = 30 * SECOND;
const DEFAULT_RETRY_TIMEOUT = 2 * SECOND;
const MAX_RETRY_ATTEMPTS = 3;
export const createWebSocketClient = (timeoutConfig = {}, clientVersion = getVersion(), retryPolicy = {
    retryTimeout: DEFAULT_RETRY_TIMEOUT,
    maxRetryAttempts: MAX_RETRY_ATTEMPTS,
}, connectionCallbacks) => {
    return new ClientImpl(timeoutConfig, clientVersion, retryPolicy, connectionCallbacks);
};
class ClientImpl {
    clientVersion;
    retryPolicy;
    connection;
    messageMap;
    log = Logging.getLogger('WebSocketClient');
    idGen = 0n;
    statistics = new NetworkStats(this.log);
    constructor(timeoutConfig, clientVersion, retryPolicy, connectionCallbacks) {
        this.clientVersion = clientVersion;
        this.retryPolicy = retryPolicy;
        ClientImpl.initProtobuf();
        const { requestTimeoutMs = DEFAULT_REQUEST_TIMEOUT, requestTimeoutCheckIntervalMs = DEFAULT_REQUEST_TIMEOUT_CHECK_INTERVAL, reconnectIntervalMs = DEFAULT_RECONNECT_INTERVAL, connectTimeoutMs = DEFAULT_CONNECTION_TIMEOUT, } = timeoutConfig;
        const messageCallbacks = {
            onMessageReceived: this.onResponseReceived.bind(this),
        };
        this.connection = new ConnectionImpl(messageCallbacks, reconnectIntervalMs, connectTimeoutMs, connectionCallbacks);
        this.messageMap = new TimedMap({
            timeoutMs: requestTimeoutMs,
            timeoutCheckIntervalMs: requestTimeoutCheckIntervalMs,
            timeoutCb: (key, value) => this.requestTimedout(key, value),
        });
    }
    async connect(hostname) {
        return withStats(this.statistics.connect, async () => {
            return this.connection.connect(hostname, true);
        }, { hostname });
    }
    async disconnect() {
        return withStats(this.statistics.disconnect, async () => {
            this.messageMap.stopTimeoutCheck();
            for (const [messageId, context] of this.messageMap.getEntries()) {
                clearTimeout(context.value.timerId);
                this.rejectPromise(context.value, messageId, new ConnectionError('Request failed because the client was explicitly disconnected'));
            }
            this.clearPendingRequests();
            return this.connection.disconnect();
        });
    }
    getConnectionState() {
        return this.connection.getConnectionState();
    }
    async sendRequest(req, session) {
        const messageId = this.getNextMessageId();
        this.logMessage(LogLevel.DEBUG, 'Sending request...', messageId);
        const requestContext = this.createRequestContext(req, session);
        this.addPendingRequest(messageId, requestContext);
        this.updateRequestStats();
        const wrappedProtobufMessage = this.craeteProtobufMessage(req, messageId, session);
        await this.doSendRequest(wrappedProtobufMessage, messageId);
        return requestContext.response;
    }
    doRetryRequest(reqContext) {
        const messageId = this.getNextMessageId();
        this.logMessage(LogLevel.DEBUG, 'Retrying request...', messageId);
        this.updateRequestContext(reqContext);
        this.addPendingRequest(messageId, reqContext);
        this.updateRetryStats();
        const wrappedProtobufMessage = this.craeteProtobufMessage(reqContext.request, messageId, reqContext.session);
        void this.doSendRequest(wrappedProtobufMessage, messageId);
    }
    craeteProtobufMessage(req, messageId, session) {
        const requestMessage = req.createMessage(messageId, this.clientVersion);
        if (!isNil(session)) {
            requestMessage.sessionToken = session;
        }
        const serializedProtobufRequest = ServiceBaseProto.RequestMsg.encode(requestMessage).finish();
        const wrappedProtobufMessage = WrappedProtobufMessage.wrap(serializedProtobufRequest, req.getDestination());
        return wrappedProtobufMessage;
    }
    async doSendRequest(wrappedProtobufMessage, messageId) {
        try {
            await this.connection.sendMessage(wrappedProtobufMessage);
        }
        catch (err) {
            assertCanHandle(err, canHandleAllErrors);
            this.logMessage(LogLevel.ERROR, 'Failed to send request.', messageId, { errorMessage: String(err) });
            this.retryRequestIfNeeded(messageId);
        }
    }
    retryRequestIfNeeded(messageId) {
        const reqContext = this.messageMap.get(messageId);
        this.removePendingRequest(messageId);
        const shouldRetry = !isNil(reqContext) && this.shouldRetry(reqContext);
        const hasRetried = !isNil(reqContext) && reqContext.retryCount;
        if (shouldRetry) {
            reqContext.timerId = setTimeout(() => this.doRetryRequest(reqContext), this.retryPolicy.retryTimeout);
        }
        else if (hasRetried) {
            reqContext.promiseResolver.reject(new ConnectionError('Failed to send message after retry'));
        }
        else {
            throw new ConnectionError('Failed to send message');
        }
    }
    static initProtobuf() {
        protobufjs.util.Long = Long;
        protobufjs.configure();
    }
    addPendingRequest(messageId, requestContext) {
        this.messageMap.set(messageId, requestContext);
        this.updateStats();
    }
    removePendingRequest(messageId) {
        this.messageMap.delete(messageId);
        this.updateStats();
    }
    clearPendingRequests() {
        this.messageMap.clear();
        this.updateStats();
    }
    updateStats() {
        this.statistics.pendingRequests = this.messageMap.size();
    }
    updateRequestStats() {
        this.statistics.requests++;
    }
    updateRetryStats() {
        this.statistics.retries++;
    }
    updateTimeoutStats() {
        this.statistics.timeouts++;
    }
    get isConnected() {
        return this.connection.getConnectionState() === ConnectionState.CONNECTED;
    }
    shouldRetry(reqContext) {
        return this.isConnected && !reqContext.hasTimedOut && reqContext.retryCount < this.retryPolicy.maxRetryAttempts;
    }
    stats() {
        return this.statistics;
    }
    createRequestContext(req, session) {
        let promiseResolver;
        const response = new Promise((resolve, reject) => {
            promiseResolver = { resolve: resolve, reject: reject };
        });
        // the caller needs to deal with the potential errors, we can't do anything here, it's a part of the business logic
        response.catch(() => { });
        return {
            request: req,
            response: response,
            promiseResolver: promiseResolver,
            dispatchTimeMs: Date.now(),
            session: session,
            retryCount: 0,
            hasTimedOut: false,
        };
    }
    updateRequestContext(reqContext) {
        reqContext.retryCount++;
        reqContext.dispatchTimeMs = Date.now();
    }
    onRequestTimeout(messageId, reqContext) {
        this.logMessage(LogLevel.WARNING, 'Timeout occurred for request.', messageId);
        this.updateTimeoutStats();
        if (this.shouldRetry(reqContext)) {
            reqContext.hasTimedOut = true;
            this.doRetryRequest(reqContext);
        }
        else {
            reqContext.promiseResolver.reject(new RequestTimeoutError(messageId));
        }
    }
    requestTimedout(key, value) {
        this.onRequestTimeout(key, value);
        return TimedMapCallbackAction.REMOVE_FROM_MAP;
    }
    getNextMessageId() {
        const ret = this.idGen;
        this.idGen = this.idGen + 1n;
        return ret;
    }
    onResponseReceived(rawResponse) {
        let decodedMessage;
        try {
            decodedMessage = this.decodeRawResponse(rawResponse);
        }
        catch (err) {
            assertCanHandle(err, canHandleAllErrors);
            this.logMessage(LogLevel.ERROR, 'Error while decoding response.', -1n, { errorMessage: err });
            return;
        }
        this.processReceivedResponse(decodedMessage);
    }
    decodeRawResponse(rawResponse) {
        const protobufMessage = WrappedProtobufMessage.unwrap(rawResponse);
        const decodedMessage = ServiceBaseProto.ResponseMsg.decode(protobufMessage);
        return decodedMessage;
    }
    processReceivedResponse(response) {
        const id = response.id;
        const messageId = longToBigInt(id);
        this.logMessage(LogLevel.DEBUG, 'Received response for request.', messageId);
        const requestContext = this.findRequestContext(messageId);
        if (isNil(requestContext)) {
            return;
        }
        const remoteVersion = this.extractRemoteVersion(response);
        if (this.handleIncompatibleVersion(remoteVersion, requestContext, messageId)) {
            return;
        }
        if (this.handleErrorResponse(response, requestContext, messageId)) {
            return;
        }
        this.completeRequest(requestContext, messageId, response);
    }
    findRequestContext(messageId) {
        const requestContext = this.messageMap.get(messageId);
        if (!isNil(requestContext)) {
            return requestContext;
        }
        this.logMessage(LogLevel.WARNING, 'Invalid messageId or the request had a timeout.', messageId);
        return undefined;
    }
    extractRemoteVersion(response) {
        return {
            major: response.versionMajor,
            minor: response.versionMinor,
            build: response.versionBuild,
        };
    }
    handleIncompatibleVersion(remoteVersion, reqContext, messageId) {
        if (this.isVersionCompatible(remoteVersion)) {
            return false;
        }
        const errorMessage = `Client version (${this.clientVersion.major}.${this.clientVersion.minor}) is not compatible
                              with the hub version (${remoteVersion.major}.${remoteVersion.minor}).`;
        this.logMessage(LogLevel.ERROR, errorMessage, messageId);
        this.rejectPromise(reqContext, messageId, new ClientVersionError(errorMessage));
        return true;
    }
    handleErrorResponse(response, reqContext, messageId) {
        const responseError = response.error;
        if (responseError.code === ServiceBaseProto.ResponseError.ErrorCode.SUCCESS) {
            return false;
        }
        this.logMessage(LogLevel.INFO, 'Received error response for request.', messageId, {
            errorCode: response.error.code,
        });
        const error = this.createResponseError(responseError);
        this.rejectPromise(reqContext, messageId, error);
        return true;
    }
    resolvePromise(reqContext, messageId, responseMessage) {
        const duration = Date.now() - reqContext.dispatchTimeMs;
        this.logMessage(LogLevel.TRACE, 'Resolving promise for request.', messageId, { duration: duration });
        reqContext.promiseResolver.resolve(responseMessage);
        this.removePendingRequest(messageId);
    }
    rejectPromise(reqContext, messageId, error) {
        const duration = Date.now() - reqContext.dispatchTimeMs;
        this.logMessage(LogLevel.TRACE, 'Rejecting request due to an error.', messageId, {
            errorMessage: error.message,
            duration: duration,
        });
        reqContext.promiseResolver.reject(error);
        this.removePendingRequest(messageId);
    }
    completeRequest(reqContext, messageId, response) {
        this.logMessage(LogLevel.DEBUG, 'Request successfully processed.', messageId);
        try {
            const responseMessage = reqContext.request.createResponse(response);
            this.resolvePromise(reqContext, messageId, responseMessage);
        }
        catch (err) {
            assertCanHandle(err, (e) => e instanceof Error);
            this.logMessage(LogLevel.ERROR, 'Unexpected error occurred while creating the response message. Promise will be rejected.', messageId, { errorMessage: err.message });
            this.rejectPromise(reqContext, messageId, err);
        }
    }
    isVersionCompatible(remoteVersion) {
        if (remoteVersion.major > this.clientVersion.major) {
            return true;
        }
        if (remoteVersion.major < this.clientVersion.major) {
            return false;
        }
        if (remoteVersion.minor > this.clientVersion.minor) {
            return true;
        }
        if (remoteVersion.minor < this.clientVersion.minor) {
            return false;
        }
        return true; // Equal
    }
    createResponseError(error) {
        const errorCode = error.code;
        const errorMessage = `[Remote] ${error.details ? error.details : ''}`;
        // Order is important - we should check the extensions first.
        // This is because the message will always have one of the error codes below but
        // the hub could add an extension to the response to show some additional information.
        if (error['.ConflictErrorResponseMsg.conflictErrorResponse']) {
            return new ConflictError(errorMessage);
        }
        return this.errorCodeToError(errorCode, errorMessage);
    }
    errorCodeToError(errorCode, errorMessage) {
        switch (errorCode) {
            case ServiceBaseProto.ResponseError.ErrorCode.INTERNAL_ERROR:
                return new InternalError(errorMessage);
            case ServiceBaseProto.ResponseError.ErrorCode.IO_ERROR:
                return new IoError(errorMessage);
            case ServiceBaseProto.ResponseError.ErrorCode.INVALID_KEY:
                return new InvalidKeyError(errorMessage);
            case ServiceBaseProto.ResponseError.ErrorCode.INVALID_ARGUMENT:
                return new InvalidArgumentError(errorMessage);
            case ServiceBaseProto.ResponseError.ErrorCode.INVALID_STATE:
                return new InvalidStateError(errorMessage);
            case ServiceBaseProto.ResponseError.ErrorCode.UNAUTHORIZED:
                return new UnauthorizedError(errorMessage);
            case ServiceBaseProto.ResponseError.ErrorCode.EPOCH_ERROR:
                return new FilespaceEpochError(errorMessage);
            case ServiceBaseProto.ResponseError.ErrorCode.TERMINATE_CLIENT:
                return new TerminateClientError(errorMessage);
            case ServiceBaseProto.ResponseError.ErrorCode.UNKNOWN:
                return new UnknownError(errorMessage);
            default:
                return new ErrorResponseError(errorCode, errorMessage);
        }
    }
    logMessage(level, message, messageId, context) {
        if (!isLevelEnabled(level, this.log)) {
            return;
        }
        this.doLogMessage(level, message, {
            endpoint: this.connection.getEndpoint(),
            messageId: messageId.toString(),
            ...context,
        });
    }
    doLogMessage(level, message, context) {
        switch (level) {
            case LogLevel.TRACE:
                this.log.trace(context, message);
                break;
            case LogLevel.DEBUG:
                this.log.debug(context, message);
                break;
            case LogLevel.INFO:
                this.log.info(context, message);
                break;
            case LogLevel.WARNING:
                this.log.warn(context, message);
                break;
            case LogLevel.ERROR:
                this.log.error(context, message);
                break;
        }
    }
}
